import { useEffect, useRef, useState } from "react";
import { Linking, ScrollView, View, useWindowDimensions } from "react-native";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  DialogTitleText,
  DialogBodyText,
  CardSurface,
  FieldMessage,
  InlineOptionPicker,
  MetaText,
  MoonFormField,
  MoonFilterChip,
  MoonValueInput,
  PrimaryButton,
  TextField,
} from "@louise/moon-ui";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@louise/moon-ui/primitives/Accordion";
import { InfoButton } from "../InfoSheet";
import { useClinicIVFTranslations } from "@louise/clinics/prediction/useClinicIVFTranslations";
import {
  estimateCDCIVF,
  type CDCIVFDiagnosis,
} from "@louise/clinics/prediction/cdcIVFEstimate";
import ivfIllustration from "../../../assets/images/steps/fertilization.png";
import { usePatientNoteDraft } from "@src/packages/care/hooks/usePatientNoteDraft";

const SOURCE_URL = "https://www.cdc.gov/art/ivf-success-estimator/index.html";
const DIAGNOSES: readonly CDCIVFDiagnosis[] = [
  "male_factor",
  "endometriosis",
  "tubal",
  "pco",
  "diminished_ovarian_reserve",
  "uterine",
  "other",
  "unexplained",
];
type EstimateField =
  | "age"
  | "measurements"
  | "ivf"
  | "eggs"
  | "donor_transfer"
  | "pregnancies"
  | "births"
  | "diagnoses";

/** A national published model, with explicit inputs and no clinic adjustment. */
export default function ClinicIVFEstimateDialog({
  onClose,
}: {
  onClose: () => void;
}) {
  const t = useClinicIVFTranslations();
  const { height: windowHeight } = useWindowDimensions();
  const copy = (key: string) => t(`clinic_ivf_estimate.${key}`);
  const scroll = useRef<ScrollView>(null);
  const [age, setAge] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [pastIVF, setPastIVF] = useState<boolean | null>(null);
  const [donor, setDonor] = useState<boolean | null>(null);
  const [pastDonorTransfer, setPastDonorTransfer] = useState<boolean | null>(
    null,
  );
  const [pregnancies, setPregnancies] = useState<0 | 1 | 2 | null>(null);
  const [births, setBirths] = useState<0 | 1 | 2 | null>(null);
  const [diagnoses, setDiagnoses] = useState<CDCIVFDiagnosis[]>([]);
  const [unknownDiagnosis, setUnknownDiagnosis] = useState(false);
  const [result, setResult] = useState<ReturnType<typeof estimateCDCIVF>>(null);
  const [modelInfoOpen, setModelInfoOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EstimateField | null>(null);
  const healthNote = usePatientNoteDraft();
  const number = (value: string) => Number(value.replace(",", "."));
  const heightCm = number(height);
  const weightKg = number(weight);
  const measurementsValid =
    heightCm >= 121.92 &&
    heightCm <= 241.3 &&
    weightKg >= 80 / 2.2046226218 &&
    weightKg <= 300 / 2.2046226218;
  const population = donor
    ? "new_donor_eggs"
    : pastIVF
      ? "returning_own_eggs"
      : "new_own_eggs";
  const modelInput =
    pregnancies !== null &&
    births !== null &&
    donor !== null &&
    (!donor || pastDonorTransfer === false) &&
    pastIVF !== null &&
    measurementsValid &&
    (unknownDiagnosis || diagnoses.length > 0)
      ? ({
          age: number(age),
          bmi: (weightKg * 2.2046226218 * 703) / (heightCm / 2.54) ** 2,
          population,
          pregnancies,
          liveBirths: births,
          diagnoses: unknownDiagnosis ? null : diagnoses,
        } as const)
      : null;
  const candidate = modelInput ? estimateCDCIVF(modelInput) : null;
  const compactLayout = windowHeight < 640;
  const historyOptions = ([0, 1, 2] as const).map((value) => ({
    id: String(value),
    label: value === 2 ? copy("two_or_more") : String(value),
    action: "choose_ivf_reproductive_history",
  }));
  const eligibilityError =
    donor && pastDonorTransfer
      ? copy("returning_donor_not_supported")
      : !donor && pastIVF && age && (number(age) < 24 || number(age) > 48)
        ? copy("returning_age_range")
        : null;
  useEffect(() => {
    if (result || !editTarget) {
      scroll.current?.scrollTo({ y: 0, animated: false });
    }
  }, [result, editTarget]);
  const editDetails = (field: EstimateField) => {
    setEditTarget(field);
    setResult(null);
  };
  const revealField = (field: EstimateField, y: number) => {
    if (editTarget === field) {
      scroll.current?.scrollTo({ y, animated: false });
    }
  };
  const detailTags =
    modelInput && result
      ? ([
          {
            field: "age",
            label: t("clinic_ivf_estimate.age_used", {
              params: { age: String(modelInput.age) },
            }),
          },
          {
            field: "measurements",
            label: t("clinic_ivf_estimate.bmi_used_value", {
              params: { bmi: String(result.bmiUsed) },
            }),
          },
          { field: "eggs", label: copy(donor ? "donor_eggs" : "own_eggs") },
          {
            field: donor ? "donor_transfer" : "ivf",
            label: copy(
              donor
                ? "first_donor_transfer_course"
                : pastIVF
                  ? "previous_ivf_course"
                  : "first_ivf_course",
            ),
          },
          {
            field: "pregnancies",
            label: t("clinic_ivf_estimate.pregnancies_used", {
              params: {
                count:
                  modelInput.pregnancies === 2
                    ? copy("two_or_more")
                    : String(modelInput.pregnancies),
              },
            }),
          },
          {
            field: "births",
            label: t("clinic_ivf_estimate.births_used", {
              params: {
                count:
                  modelInput.liveBirths === 2
                    ? copy("two_or_more")
                    : String(modelInput.liveBirths),
              },
            }),
          },
        ] satisfies { field: EstimateField; label: string }[])
      : [];

  return (
    <Dialog
      visible
      onClose={onClose}
      layout="centered"
      closeButton="minimal"
      testID="clinic-ivf-estimate-dialog"
      contentStyle={
        compactLayout ? { paddingTop: 20, paddingBottom: 20 } : undefined
      }
    >
      <View style={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
        <InfoButton
          accessibilityLabel={copy("about_cdc_model")}
          action="open_cdc_ivf_model_info"
          testID="cdc-ivf-model-info"
          onPress={() => setModelInfoOpen(true)}
        />
      </View>
      <DialogIcon
        source={ivfIllustration}
        width={compactLayout ? 48 : 64}
        height={compactLayout ? 48 : 64}
      />
      <DialogHeader
        align="center"
        title={copy("national_ivf_estimate")}
        subtitle={copy("national_model_scope")}
        style={{
          marginTop: compactLayout ? 8 : 12,
          marginBottom: compactLayout ? 8 : 18,
        }}
      />
      <ScrollView
        key={result ? "results" : "inputs"}
        ref={scroll}
        style={{ flexShrink: 1 }}
        contentContainerStyle={{ gap: result ? 12 : 18, paddingBottom: 8 }}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {result ? (
          <View style={{ gap: 12 }} testID="clinic-ivf-estimate-result">
            <CardSurface
              variant="panel"
              testID="clinic-ivf-estimate-probabilities"
              style={{ padding: 12, gap: 12 }}
            >
              <DialogBodyText style={{ marginTop: 0, textAlign: "center" }}>
                {copy("chance_of_live_birth")}
              </DialogBodyText>
              <View style={{ flexDirection: "row", gap: 12 }}>
                {result.probabilities.map((rate, index) => (
                  <View key={index} style={{ flex: 1, gap: 4 }}>
                    <DialogTitleText style={{ textAlign: "center" }}>
                      {`${Math.round(rate * 100)}%`}
                    </DialogTitleText>
                    <DialogBodyText
                      style={{ marginTop: 0, textAlign: "center" }}
                    >
                      {t(
                        `clinic_ivf_estimate.${donor ? (index === 0 ? "after_one_donor_transfer" : "after_multiple_donor_transfers") : pastIVF ? (index === 0 ? "after_one_additional_retrieval" : "after_multiple_additional_retrievals") : index === 0 ? "after_one_retrieval" : "after_multiple_retrievals"}`,
                        { params: { count: String(index + 1) } },
                      )}
                    </DialogBodyText>
                  </View>
                ))}
              </View>
            </CardSurface>
            <Accordion type="single" collapsible>
              <AccordionItem value="details-used" testID="cdc-ivf-details-used">
                <AccordionTrigger
                  testID="cdc-ivf-details-toggle"
                  accessibilityLabel={copy("details_used_for_estimate")}
                  analyticsLabel={copy("details_used_for_estimate")}
                  action={{
                    expand: "expand_cdc_ivf_estimate_details",
                    collapse: "collapse_cdc_ivf_estimate_details",
                  }}
                >
                  <DialogBodyText style={{ marginTop: 0, flex: 1 }}>
                    {copy("details_used_for_estimate")}
                  </DialogBodyText>
                </AccordionTrigger>
                <AccordionContent testID="cdc-ivf-details-content">
                  <View style={{ gap: 8 }}>
                    <View
                      style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}
                    >
                      {detailTags.map(({ field, label }) => (
                        <MoonFilterChip
                          key={field}
                          label={label}
                          selected={false}
                          tone="outline"
                          accessibilityLabel={t(
                            "clinic_ivf_estimate.edit_estimate_detail",
                            {
                              params: { detail: label },
                            },
                          )}
                          action="edit_cdc_ivf_estimate_detail"
                          testID={`cdc-ivf-detail-${field}`}
                          onPress={() => editDetails(field)}
                        />
                      ))}
                      {unknownDiagnosis ? (
                        <MoonFilterChip
                          label={copy("diagnosis_not_known")}
                          selected={false}
                          tone="outline"
                          accessibilityLabel={t(
                            "clinic_ivf_estimate.edit_estimate_detail",
                            { params: { detail: copy("diagnosis_not_known") } },
                          )}
                          action="edit_cdc_ivf_estimate_reason"
                          testID="cdc-ivf-detail-unknown"
                          onPress={() => editDetails("diagnoses")}
                        />
                      ) : (
                        diagnoses.map((diagnosis) => (
                          <MoonFilterChip
                            key={diagnosis}
                            label={copy(`diagnosis.${diagnosis}`)}
                            selected
                            showCheck={false}
                            tone="outline"
                            accessibilityLabel={t(
                              "clinic_ivf_estimate.edit_estimate_detail",
                              {
                                params: {
                                  detail: copy(`diagnosis.${diagnosis}`),
                                },
                              },
                            )}
                            action="edit_cdc_ivf_estimate_reason"
                            testID={`cdc-ivf-detail-${diagnosis}`}
                            onPress={() => editDetails("diagnoses")}
                          />
                        ))
                      )}
                    </View>
                    {unknownDiagnosis && (
                      <DialogBodyText style={{ marginTop: 0 }}>
                        {copy("estimate_without_diagnosis_details")}
                      </DialogBodyText>
                    )}
                    <View style={{ gap: 12 }} testID="cdc-ivf-health-note">
                      <View testID="cdc-ivf-health-note-field">
                        <TextField
                          notes
                          label={copy("health_notes")}
                          value={healthNote.text}
                          onChangeText={healthNote.onChangeText}
                          disabled={healthNote.saving}
                          error={
                            healthNote.failed
                              ? copy("health_note_save_failed")
                              : undefined
                          }
                          placeholder={copy("add_health_note")}
                          accessibilityLabel={copy("health_notes")}
                          testID="cdc-ivf-health-note-input"
                        />
                      </View>
                      {healthNote.needsSignIn && !!healthNote.text.trim() && (
                        <DialogBodyText style={{ marginTop: 0 }}>
                          {copy("sign_in_to_save_health_note")}
                        </DialogBodyText>
                      )}
                      {healthNote.saved && (
                        <View testID="cdc-ivf-health-note-saved">
                          <DialogBodyText style={{ marginTop: 0 }}>
                            {copy("health_note_saved")}
                          </DialogBodyText>
                        </View>
                      )}
                      <PrimaryButton
                        label={copy("save_health_note")}
                        action="save_patient_health_note"
                        disabled={!healthNote.canSave}
                        loading={healthNote.saving}
                        onPress={() => void healthNote.save()}
                        testID="cdc-ivf-save-health-note"
                      />
                    </View>
                  </View>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </View>
        ) : (
          <>
            <DialogBodyText>{copy("review_your_details")}</DialogBodyText>
            <View
              onLayout={({ nativeEvent }) =>
                revealField("age", nativeEvent.layout.y)
              }
            >
              <MoonFormField label={copy("age_in_years")} required>
                <MoonValueInput
                  value={age}
                  onChangeText={setAge}
                  keyboardType="number-pad"
                  accessibilityLabel={copy("age_in_years")}
                  testID="cdc-ivf-age"
                />
              </MoonFormField>
            </View>
            <View
              style={{ flexDirection: "row", gap: 12 }}
              onLayout={({ nativeEvent }) =>
                revealField("measurements", nativeEvent.layout.y)
              }
            >
              <MoonFormField
                label={copy("height_cm")}
                required
                style={{ flex: 1 }}
              >
                <MoonValueInput
                  value={height}
                  onChangeText={setHeight}
                  keyboardType="decimal-pad"
                  suffix="cm"
                  accessibilityLabel={copy("height_cm")}
                  testID="cdc-ivf-height"
                />
              </MoonFormField>
              <MoonFormField
                label={copy("weight_kg")}
                required
                style={{ flex: 1 }}
              >
                <MoonValueInput
                  value={weight}
                  onChangeText={setWeight}
                  keyboardType="decimal-pad"
                  suffix="kg"
                  accessibilityLabel={copy("weight_kg")}
                  testID="cdc-ivf-weight"
                />
              </MoonFormField>
            </View>
            <View
              onLayout={({ nativeEvent }) =>
                revealField("ivf", nativeEvent.layout.y)
              }
            >
              <MoonFormField label={copy("previous_ivf_use")} required>
                <InlineOptionPicker
                  options={[
                    {
                      id: "first",
                      label: copy("first_ivf_treatment"),
                      action: "choose_ivf_history",
                    },
                    {
                      id: "previous",
                      label: copy("used_ivf_before"),
                      action: "choose_ivf_history",
                    },
                  ]}
                  value={pastIVF === null ? "" : pastIVF ? "previous" : "first"}
                  onChange={(value) => setPastIVF(value === "previous")}
                />
              </MoonFormField>
            </View>
            <View
              onLayout={({ nativeEvent }) =>
                revealField("eggs", nativeEvent.layout.y)
              }
            >
              <MoonFormField
                label={copy("egg_source")}
                required
                hint={donor ? copy("first_donor_course") : undefined}
              >
                <InlineOptionPicker
                  options={[
                    {
                      id: "own",
                      label: copy("own_eggs"),
                      action: "choose_ivf_egg_source",
                    },
                    {
                      id: "donor",
                      label: copy("donor_eggs"),
                      action: "choose_ivf_egg_source",
                    },
                  ]}
                  value={donor === null ? "" : donor ? "donor" : "own"}
                  onChange={(value) => setDonor(value === "donor")}
                />
              </MoonFormField>
            </View>
            {donor && (
              <View
                onLayout={({ nativeEvent }) =>
                  revealField("donor_transfer", nativeEvent.layout.y)
                }
              >
                <MoonFormField
                  label={copy("previous_donor_transfers")}
                  required
                >
                  <InlineOptionPicker
                    options={[
                      {
                        id: "first",
                        label: copy("first_donor_transfer"),
                        action: "choose_donor_transfer_history",
                      },
                      {
                        id: "previous",
                        label: copy("had_donor_transfer_before"),
                        action: "choose_donor_transfer_history",
                      },
                    ]}
                    value={
                      pastDonorTransfer === null
                        ? ""
                        : pastDonorTransfer
                          ? "previous"
                          : "first"
                    }
                    onChange={(value) =>
                      setPastDonorTransfer(value === "previous")
                    }
                  />
                </MoonFormField>
              </View>
            )}
            <View
              onLayout={({ nativeEvent }) =>
                revealField("pregnancies", nativeEvent.layout.y)
              }
            >
              <MoonFormField
                label={copy("previous_pregnancies")}
                required
                testID="cdc-ivf-pregnancies"
              >
                <InlineOptionPicker
                  options={historyOptions}
                  value={pregnancies === null ? "" : String(pregnancies)}
                  onChange={(value) => {
                    const count = ([0, 1, 2] as const).find(
                      (count) => String(count) === value,
                    );
                    if (count !== undefined) setPregnancies(count);
                  }}
                />
              </MoonFormField>
            </View>
            <View
              onLayout={({ nativeEvent }) =>
                revealField("births", nativeEvent.layout.y)
              }
            >
              <MoonFormField
                label={copy("previous_live_births")}
                required
                testID="cdc-ivf-births"
                error={
                  pregnancies !== null &&
                  births !== null &&
                  births > pregnancies
                    ? copy("births_cannot_exceed_pregnancies")
                    : undefined
                }
              >
                <InlineOptionPicker
                  options={historyOptions}
                  value={births === null ? "" : String(births)}
                  onChange={(value) => {
                    const count = ([0, 1, 2] as const).find(
                      (count) => String(count) === value,
                    );
                    if (count !== undefined) setBirths(count);
                  }}
                />
              </MoonFormField>
            </View>
            <View
              onLayout={({ nativeEvent }) =>
                revealField("diagnoses", nativeEvent.layout.y)
              }
            >
              <MoonFormField
                label={copy("reason_for_ivf")}
                required
                testID="cdc-ivf-reasons"
              >
                <InlineOptionPicker
                  wrap
                  columns={2}
                  options={[
                    ...DIAGNOSES.map((diagnosis) => ({
                      id: diagnosis,
                      label: copy(`diagnosis.${diagnosis}`),
                      action: "choose_ivf_diagnosis",
                    })),
                    {
                      id: "unknown",
                      label: copy("diagnosis_not_known"),
                      action: "choose_ivf_diagnosis",
                    },
                  ]}
                  value={unknownDiagnosis ? ["unknown"] : diagnoses}
                  onChange={(value) => {
                    if (value === "unknown") {
                      setUnknownDiagnosis(!unknownDiagnosis);
                      setDiagnoses([]);
                    } else {
                      const diagnosis = DIAGNOSES.find(
                        (diagnosis) => diagnosis === value,
                      );
                      if (diagnosis) {
                        setUnknownDiagnosis(false);
                        setDiagnoses((current) =>
                          diagnosis === "unexplained"
                            ? current.includes(diagnosis)
                              ? []
                              : [diagnosis]
                            : current.includes(diagnosis)
                              ? current.filter((value) => value !== diagnosis)
                              : [
                                  ...current.filter(
                                    (value) => value !== "unexplained",
                                  ),
                                  diagnosis,
                                ],
                        );
                      }
                    }
                  }}
                />
              </MoonFormField>
            </View>
            <MetaText>{copy("supported_inputs")}</MetaText>
          </>
        )}
      </ScrollView>
      {!result && eligibilityError && (
        <FieldMessage style={{ marginTop: 12 }}>
          {eligibilityError}
        </FieldMessage>
      )}
      {!result && (
        <DialogActions style={{ marginTop: 16 }}>
          <PrimaryButton
            label={copy("calculate_your_estimate")}
            action="calculate_cdc_ivf_estimate"
            disabled={!candidate}
            onPress={() => {
              setEditTarget(null);
              setResult(candidate);
            }}
            testID="cdc-ivf-calculate"
          />
        </DialogActions>
      )}
      <Dialog
        visible={modelInfoOpen}
        onClose={() => setModelInfoOpen(false)}
        layout="centered"
        closeButton="minimal"
        closeAction="close_cdc_ivf_model_info"
        backdropPressLabel="dismiss_cdc_ivf_model_info"
        testID="cdc-ivf-model-info-dialog"
      >
        <DialogHeader
          title={copy("about_cdc_model")}
          style={{ marginBottom: 16 }}
        />
        <ScrollView
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 12 }}
          showsVerticalScrollIndicator={false}
        >
          {(!result || !donor) && (
            <DialogBodyText style={{ marginTop: 0 }}>
              {copy("own_egg_time_horizon")}
            </DialogBodyText>
          )}
          {(!result || donor) && (
            <DialogBodyText style={{ marginTop: 0 }}>
              {copy("donor_time_horizon")}
            </DialogBodyText>
          )}
          <DialogBodyText style={{ marginTop: 0 }}>
            {result
              ? t("clinic_ivf_estimate.bmi_used", {
                  params: { bmi: String(result.bmiUsed) },
                })
              : copy("supported_inputs")}
          </DialogBodyText>
          <DialogBodyText style={{ marginTop: 0 }}>
            {copy("national_data_context")}
          </DialogBodyText>
          <DialogBodyText style={{ marginTop: 0 }}>
            {copy("discuss_with_your_clinic")}
          </DialogBodyText>
        </ScrollView>
        <DialogActions style={{ marginTop: 16 }}>
          <PrimaryButton
            label={copy("open_cdc_ivf_estimator")}
            accessibilityHint={copy("opens_cdc_website_in_browser")}
            action="view_cdc_ivf_estimator"
            onPress={() => Linking.openURL(SOURCE_URL)}
          />
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
